import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ScrollText, Loader2, Search, RefreshCw, Filter } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/auditoria`;

const CORES_MODULO = {
  carteira: "bg-emerald-600",
  "carteira-usd": "bg-cyan-600",
  planos: "bg-purple-600",
  usuarios: "bg-orange-600",
};

const AuditoriaCentral = () => {
  const [registros, setRegistros] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [moduloFiltro, setModuloFiltro] = useState("");
  const [buscaEmail, setBuscaEmail] = useState("");
  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams();
      if (moduloFiltro) params.set("modulo", moduloFiltro);
      if (buscaEmail) params.set("usuario_email", buscaEmail);

      const [respRegistros, respModulos] = await Promise.all([
        authService.fetchAuth(`${API}/central?${params}`),
        authService.fetchAuth(`${API}/modulos-disponiveis`),
      ]);
      if (respRegistros.ok) setRegistros(await respRegistros.json());
      if (respModulos.ok) setModulos(await respModulos.json());
    } catch (error) {
      console.error("Erro ao carregar auditoria:", error);
      toast.error("Não foi possível carregar a auditoria.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line
  }, [moduloFiltro]);

  const formatarData = (isoString) => {
    try {
      return new Date(isoString).toLocaleString("pt-BR");
    } catch {
      return isoString;
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ScrollText className="w-7 h-7 text-orange-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Auditoria Central</h1>
            <p className="text-sm text-gray-400">Quem fez o quê, quando, em todo o sistema</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregar}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Buscar por e-mail..."
            value={buscaEmail}
            onChange={(e) => setBuscaEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && carregar()}
            className="pl-9"
          />
        </div>
        <select
          value={moduloFiltro}
          onChange={(e) => setModuloFiltro(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-md px-3 text-white text-sm"
        >
          <option value="">Todos os módulos</option>
          {modulos.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : registros.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          <Filter className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          Nenhum registro de auditoria ainda.
        </Card>
      ) : (
        <div className="space-y-2">
          {registros.map((r) => (
            <Card key={r.id} className="p-4 bg-gray-800/50 border-gray-700">
              <div className="flex items-center justify-between mb-1">
                <p className="text-white font-medium">{r.acao}</p>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded text-white ${CORES_MODULO[r.modulo] || "bg-gray-600"}`}>
                  {r.modulo}
                </span>
              </div>
              <p className="text-xs text-gray-400">{r.usuario_email} · {formatarData(r.created_at)}</p>
              {r.detalhes && <p className="text-xs text-gray-500 mt-1">{r.detalhes}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditoriaCentral;
